<script setup lang="ts">
import { propsFactory } from "@soft-db/soft-utils";
import {
	area,
	curveStepAfter,
	easeLinear,
	lab,
	line,
	pointer,
	select,
	symbol,
	symbolCircle,
	symbolCross,
	symbolDiamond,
	symbolSquare,
	symbolStar,
	symbolTriangle,
	type Numeric,
} from "d3";
import { v4 as uuidv4 } from "uuid";
import { computed, ref, watch, type PropType } from "vue";
import type { DataPoint, LinePathOptions, LinePathStyle, PointShape } from "../interfaces";
import { serieScaleProps, useSerieScale } from "../use/serie-scale";
import { useSVGContainer } from "../use/svg-container";

const props = defineProps(
	propsFactory<Omit<LinePathOptions, "style">>()({
		id: {
			type: String,
			required: true,
		},
		label: {
			type: String,
		},
		data: {
			type: Array as PropType<DataPoint[]>,
			default: () => [],
		},
		live: Boolean,
		animation: Boolean,
		active: Boolean,
		disabled: Boolean,
		step: Boolean,
		curveStyle: {
			type: Object as PropType<LinePathStyle>,
			required: true,
			default: () => ({}),
		},
		...serieScaleProps,
	})
);

const emit = defineEmits<{
	(e: "click", payload: { x: Numeric; y: Numeric }): void;
	(e: "hover", payload: boolean): void;
}>();

const uid = uuidv4();
const pathRef = ref<SVGPathElement>();

const { xScale, yScale } = useSerieScale(props);
const { contentDimension, clickOutsideEvent } = useSVGContainer();

const curve = computed(() => {
	if (props.step) return curveStepAfter;
	return null;
});

const getX = (d: DataPoint) => {
	if (!xScale.value) return 0;

	return xScale.value(d.x);
};

const getY = (d: DataPoint) => {
	if (!yScale.value || d.y == null || !yScale.value(d.y)) return 0;

	return yScale.value(d.y);
};

const validDataPoint = (d: DataPoint) => {
	const chartX = getX(d);
	const chartY = getY(d);

	const validChartPoint = !isNaN(chartX) && !isNaN(chartY);

	return d.y != null && !isNaN(d.y) && !!xScale && !!yScale && validChartPoint;
};

const d3Area = computed(() => {
	const reverse = props.curveStyle.fill?.type === "end";
	return area<DataPoint>()
		.x(getX)
		.y0(reverse ? 0 : contentDimension.value.height)
		.y1(getY)
		.defined(validDataPoint);
});

const d3Line = computed(() => {
	let l = line<DataPoint>().x(getX).y(getY).defined(validDataPoint);

	if (curve.value) {
		l = l.curve(curve.value);
	}
	return l;
});

const dLine = computed(() => {
	const { data } = props;

	return d3Line.value(data) || "";
});

const dArea = computed(() => {
	const { data } = props;

	return d3Area.value(data) || "";
});

function colorLab(colorTxt: string) {
	if (colorTxt.startsWith("oklch")) {
		const matches = colorTxt.match(/\((.*)\)/);

		const [l, c, h] = matches?.[1]?.split(" ") || [];

		return {
			l: l,
			c: c,
			h: h,

			// Not really hex, but will do
			formatHex() {
				return `oklch(${this.l} ${this.c} ${this.h})`;
			},

			darker() {
				if (this.l.endsWith("%")) {
					this.l = (parseFloat(this.l) / 100 - 0.1).toString();
				} else {
					this.l = (parseFloat(this.l) - 0.1).toString();
				}
				return this;
			},
		};
	} else {
		return lab(colorTxt);
	}
}

const color = computed(() => {
	const { active } = props;
	const { color } = props.curveStyle;

	let labColor = colorLab(color);
	if (active) {
		labColor = labColor.darker();
	}

	return labColor;
});

const opacity = computed(() => {
	const { disabled } = props;
	if (disabled) return 0.2;
	return null;
});

const getSymbol = (shape: PointShape) => {
	switch (shape) {
		case "circle":
			return symbolCircle;
		case "square":
			return symbolSquare;
		case "cross":
			return symbolCross;
		case "triangle":
			return symbolTriangle;
		case "star":
			return symbolStar;
		case "diamond":
			return symbolDiamond;
		default:
			return symbolCircle;
	}
};

const d3SymbolPath = (size: number) => {
	const { point } = props.curveStyle;
	const selectedSymbol = getSymbol(point.shape);

	return (
		symbol()
			.type(selectedSymbol)
			.size(50 + 3 ** size)() || ""
	);
};

type PointPosition = { id: string; cx: number; cy: number } & Record<string, unknown>;

const distanceBetweenCircles = (p1: PointPosition, p2: PointPosition) => {
	return Math.sqrt(Math.pow(p2.cx - p1.cx, 2) + Math.pow(p2.cy - p1.cy, 2));
};

const addPoint = (points: PointPosition[], currentPoint: PointPosition) => {
	const lastPoint = points[points.length - 1];
	const { lineWidth } = props.curveStyle;
	const limit = lineWidth / 2;

	// Don't add overlaping circles
	if (lastPoint && distanceBetweenCircles(lastPoint, currentPoint) < limit) {
		return;
	}

	points.push(currentPoint);
};

const pointComponent = computed(() =>
	props.curveStyle.point.shape === "circle" ? "circle" : "path"
);

const points = computed(() => {
	if (!xScale.value || !yScale.value) return [];
	let { size } = props.curveStyle.point;
	if (!size) size = 3;

	const style = {
		fill: color.value.formatHex(),
		opacity: opacity.value,
	};

	const { data } = props;
	const pointsIdSet = new Set<string>();
	const points: PointPosition[] = [];

	for (const point of data) {
		const { x, y } = point;
		if (y == null || isNaN(y)) continue;

		const cx = xScale.value(point.x);
		const cy = yScale.value(point.y || 0) || 0;

		const id = `${uid}-${x.valueOf()}-${y.valueOf()}`;
		if (pointsIdSet.has(id)) continue;

		pointsIdSet.add(id);

		const newPoint: PointPosition = {
			id,
			cx,
			cy,
		};

		if (pointComponent.value === "circle") {
			addPoint(points, {
				...newPoint,
				...style,
				r: size,
			});
		} else {
			addPoint(points, {
				...newPoint,
				...style,
				d: d3SymbolPath(size) || "",
				transform: `translate(${cx}, ${cy})`,
			});
		}
	}

	return points;
});

const selectedPoint = ref<PointPosition>();

const pointMouseOver = ref();

const handlePathHover = (hover: boolean) => {
	emit("hover", hover);
};

const handlePointHover = (id: string, hover: boolean) => {
	pointMouseOver.value = hover ? id : "";
	emit("hover", hover);
};

const clickOustideHandler = () => {
	selectedPoint.value = undefined;
};
clickOutsideEvent.on(clickOustideHandler);

const lineAttrs = computed(() => {
	const { lineWidth, antialiasing, lineDashStyle } = props.curveStyle;

	let dasharray: string | null = null;
	if (lineDashStyle && lineDashStyle.length) {
		dasharray = lineDashStyle.join(" ");
	}

	return {
		stroke: color.value.formatHex(),
		fill: "none",
		opacity: opacity.value ?? props.curveStyle.opacity ?? 1,
		"stroke-width": lineWidth,
		"shape-rendering": antialiasing ? "auto" : "crispEdges",
		"stroke-dasharray": dasharray,
	};
});

const areaAttrs = computed(() => {
	const { lineWidth, antialiasing } = props.curveStyle;

	return {
		stroke: "none",
		fill: color.value.formatHex(),
		"fill-opacity": opacity.value ?? props.curveStyle.fill?.opacity ?? 1,
		"stroke-width": lineWidth,
		"shape-rendering": antialiasing ? "auto" : "crispEdges",
		d: dArea.value,
	};
});

watch([dLine, pathRef], () => {
	if (!pathRef.value || !xScale.value) return;

	let selection = select(pathRef.value);

	if (!props.live) {
		if (props.animation) {
			selection = selection.transition() as any;
		}
		selection.attr("d", dLine.value);
	} else {
		const scaleMin = xScale.value.domain()[0];
		selection
			.attr("d", dLine.value)
			.transition()
			.duration(0)
			.attr("transform", null)
			.transition()
			.duration(1000)
			.ease(easeLinear)
			.attr("transform", `translate(${xScale.value(scaleMin.valueOf() - 1)}, 0)`);
	}
});

const clickHandler = (e: PointerEvent | MouseEvent) => {
	const point = pointer(e);
	const position = {
		x: xScale.value?.invert(point[0]) || 0,
		y: yScale.value?.invert(point[1]) || 0,
	};

	emit("click", position);
};

const showFill = computed(() => !!props.curveStyle.fill?.type);
</script>

<template>
	<g>
		<path
			ref="pathRef"
			class="pointer-events-auto"
			v-bind="lineAttrs as any"
			@click="clickHandler"
			@mouseenter="() => handlePathHover(true)"
			@mouseleave="() => handlePathHover(false)"
		/>
		<path v-if="showFill" v-bind="areaAttrs" />

		<g v-if="curveStyle.point.visible">
			<component
				v-for="point of points"
				:is="pointComponent"
				:key="point.id"
				class="pointer-events-auto"
				:style="pointMouseOver === point.id ? { filter: 'url(#drop-shadow)' } : {}"
				v-bind="point"
				@click="clickHandler"
				@mouseenter="() => handlePointHover(point.id, true)"
				@mouseleave="() => handlePointHover(point.id, false)"
			/>
		</g>
	</g>
</template>
